import { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Loader2, Trash2, CheckCircle2, LocateFixed } from "lucide-react";
import { MAP_CENTER, MAP_ZOOM } from "@/mock";
import { getIslasContenedores } from "@/api";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const islaIcon = (nearest, selected, llena) => {
  const color = selected ? "#BD3742" : llena ? "#dc2626" : "#5c927a";
  const size = nearest ? 26 : 16;
  return L.divIcon({
    className: "",
    html: nearest
      ? `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 0 0 4px ${color}55, 0 2px 6px rgba(0,0,0,0.4);"></div>`
      : `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 0 1px rgba(0,0,0,0.2);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length < 2) {
      map.setView(points[0]);
      return;
    }
    map.fitBounds(points, { padding: [50, 50], maxZoom: 17 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points)]);
  return null;
}

export default function Step3Address({ data, update, onNext, onBack }) {
  const [query, setQuery] = useState(data.direccion_completa || "");
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [islas, setIslas] = useState([]);
  const [otraIsla, setOtraIsla] = useState(false);
  const [manualIsla, setManualIsla] = useState(false);
  const [position, setPosition] = useState(
    data.latitude ? [data.latitude, data.longitude] : MAP_CENTER
  );

  useEffect(() => {
    getIslasContenedores().then(setIslas).catch(() => setIslas([]));
  }, []);

  const islasConDistancia = useMemo(() => {
    return islas
      .filter((i) => i.lat != null && i.lng != null)
      .map((i) => ({ ...i, distancia: haversineMeters(position[0], position[1], i.lat, i.lng) }))
      .sort((a, b) => a.distancia - b.distancia);
  }, [islas, position]);

  const nearestIslas = islasConDistancia.slice(0, 8);
  const islaMasCercana = islasConDistancia.find((i) => !i.llena) || islasConDistancia[0];

  // Auto-selecciona la isla disponible (no llena) más cercana cada vez que la dirección
  // cambia (búsqueda, geolocalización o clic en el mapa), salvo que el ciudadano haya
  // elegido manualmente otra isla para esta misma dirección.
  useEffect(() => {
    if (!manualIsla && islaMasCercana) {
      update({ isla_contenedores: islaMasCercana.nombre });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islaMasCercana?.id, manualIsla]);

  const islaSeleccionada = islasConDistancia.find((i) => i.nombre === data.isla_contenedores);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
        query + ", San Fernando de Henares, Madrid, España"
      )}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json && json[0]) {
        const lat = parseFloat(json[0].lat);
        const lon = parseFloat(json[0].lon);
        setPosition([lat, lon]);
        setManualIsla(false);
        update({ latitude: lat, longitude: lon, direccion_completa: query });
      }
    } catch (e) {
      console.error("Error buscando dirección", e);
    } finally {
      setLoading(false);
    }
  };

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        setPosition([lat, lon]);
        setManualIsla(false);
        update({ latitude: lat, longitude: lon });
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`;
          const res = await fetch(url);
          const json = await res.json();
          const addr = json?.address || {};
          const calle = [addr.road, addr.house_number].filter(Boolean).join(", ");
          if (calle) {
            setQuery(calle);
            update({ direccion_completa: calle });
          }
        } catch (e) {
          console.error("Error en geocodificación inversa", e);
        } finally {
          setLocating(false);
        }
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  useEffect(() => {
    update({ latitude: position[0], longitude: position[1] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canContinue = query.trim().length > 4 && (data.isla_contenedores || "").trim().length > 2;

  const fitPoints = islaMasCercana ? [position, [islaMasCercana.lat, islaMasCercana.lng]] : [position];

  return (
    <div>
      <h2 className="font-heading font-bold text-2xl text-municipal mb-2">¿Dónde recogemos?</h2>
      <p className="text-gray-500 mb-6">Busca tu dirección, usa tu ubicación actual o haz clic en el mapa.</p>

      <div className="flex gap-3 mb-5">
        <div className="flex-1">
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              update({ direccion_completa: e.target.value });
            }}
            placeholder="Calle, número..."
            data-testid="wizard-address-input"
          />
        </div>
        <Button onClick={handleSearch} disabled={loading} className="bg-municipal hover:bg-municipal-dark text-white shrink-0" data-testid="wizard-address-search-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
        <Button onClick={handleLocate} disabled={locating} variant="outline" className="border-municipal text-municipal shrink-0" data-testid="wizard-geolocate-btn" title="Usar mi ubicación actual">
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
        </Button>
      </div>

      {islaMasCercana && (
        <div className="flex items-start gap-3 bg-[#5c927a]/10 border border-[#5c927a]/40 rounded-xl p-4 mb-4" data-testid="isla-mas-cercana-callout">
          <Trash2 className="w-5 h-5 text-[#5c927a] shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-[#3f6a53]">
              Isla de contenedores más cercana: {islaMasCercana.nombre}
            </p>
            <p className="text-gray-500 text-xs mt-0.5">
              {islaMasCercana.direccion_referencia ? `${islaMasCercana.direccion_referencia} · ` : ""}
              a {Math.round(islaMasCercana.distancia)} metros de la dirección indicada
            </p>
          </div>
        </div>
      )}

      <div className="h-80 rounded-xl overflow-hidden border border-gray-200 mb-3" data-testid="wizard-map">
        <MapContainer center={position} zoom={MAP_ZOOM} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position} />
          <ClickHandler
            onPick={(pos) => {
              setPosition(pos);
              setManualIsla(false);
              update({ latitude: pos[0], longitude: pos[1] });
            }}
          />
          <FitBounds points={fitPoints} />
          {islaMasCercana && (
            <Polyline positions={[position, [islaMasCercana.lat, islaMasCercana.lng]]} pathOptions={{ color: "#5c927a", weight: 2, dashArray: "5 7" }} />
          )}
          {nearestIslas.map((isla) => {
            const esLaMasCercana = islaMasCercana?.id === isla.id;
            return (
              <Marker
                key={isla.id}
                position={[isla.lat, isla.lng]}
                icon={islaIcon(esLaMasCercana, data.isla_contenedores === isla.nombre, isla.llena)}
                eventHandlers={{ click: () => { setManualIsla(true); update({ isla_contenedores: isla.nombre }); } }}
                data-testid={`isla-marker-${isla.id}`}
              >
                <Tooltip direction="top" permanent={esLaMasCercana} opacity={1}>
                  {isla.nombre} · {Math.round(isla.distancia)} m {isla.llena ? "· LLENA" : ""}
                </Tooltip>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
      {nearestIslas.length > 0 && (
        <p className="text-xs text-gray-400 mb-5 flex items-center gap-1.5">
          <Trash2 className="w-3.5 h-3.5 text-dorado" /> Puntos verdes: islas de contenedores soterrados cercanas (el más grande es la más cercana). Haz clic en una para elegirla.
        </p>
      )}

      <div className="border-t border-gray-100 pt-5 mb-8">
        <Label className="text-sm font-semibold text-municipal mb-2 flex items-center gap-1.5">
          <Trash2 className="w-4 h-4" /> Isla de contenedores donde dejaremos aviso de recogida
        </Label>
        <p className="text-xs text-gray-400 mb-3">
          No se recoge en la puerta del domicilio: indica la isla de contenedores más cercana donde
          dejarás los elementos el día de la recogida. Hemos preseleccionado la más cercana a tu dirección.
        </p>
        {islas.length > 0 && !otraIsla ? (
          <>
            <Select
              value={data.isla_contenedores || ""}
              onValueChange={(v) => {
                if (v === "__otra__") { setOtraIsla(true); setManualIsla(true); update({ isla_contenedores: "" }); }
                else { setManualIsla(true); update({ isla_contenedores: v }); }
              }}
            >
              <SelectTrigger data-testid="wizard-isla-select">
                <SelectValue placeholder="Selecciona la isla más cercana" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {islasConDistancia.map((isla) => (
                  <SelectItem key={isla.id} value={isla.nombre}>
                    {isla.nombre} · {Math.round(isla.distancia)} m {isla.llena ? "⚠ LLENA" : ""}
                  </SelectItem>
                ))}
                <SelectItem value="__otra__">Otra (especificar)</SelectItem>
              </SelectContent>
            </Select>
            {islaSeleccionada?.llena && (
              <p className="flex items-center gap-1.5 text-xs text-red-600 mt-2 font-medium" data-testid="isla-llena-aviso">
                ⚠ Esta isla está marcada como llena temporalmente. Te recomendamos elegir otra isla cercana si es posible.
              </p>
            )}
            {data.isla_contenedores && !islaSeleccionada?.llena && (
              <p className="flex items-center gap-1.5 text-xs text-valoriza-green mt-2" data-testid="isla-seleccionada-confirmacion">
                <CheckCircle2 className="w-3.5 h-3.5" /> Isla seleccionada: {data.isla_contenedores}
              </p>
            )}
          </>
        ) : (
          <Input
            value={data.isla_contenedores || ""}
            onChange={(e) => update({ isla_contenedores: e.target.value })}
            placeholder="Ej: Isla de contenedores C/ Mayor esquina Plaza España"
            data-testid="wizard-isla-input"
          />
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="border-municipal text-municipal">Atrás</Button>
        <Button disabled={!canContinue} onClick={onNext} className="bg-municipal hover:bg-municipal-dark text-white" data-testid="wizard-step3-next">
          Continuar
        </Button>
      </div>
    </div>
  );
}
